// Only used to resolve legacy `?service=Dementia%20Care` URLs that were shared or
// bookmarked before the search moved to care_type term ids. Nothing new is written
// in this format, so this map does not need new terms adding to it.
const legacyServiceNames = {
    "-1": "What are you looking for?",
    "462": "Care for younger adults with disabilities",
    "492": "Day care",
    "461": "Dementia Care",
    "455": "Home care",
    "449": "Live-in care",
    "460": "Nursing Care",
    "547": "Nursing Dementia Care",
    "457": "Palliative Care",
    "458": "Residential Care",
    "459": "Residential Dementia Care",
    "450": "Respite Care",
    "456": "Specialist Care",
    "0": "All"
};

// How many results render before the "Show more" / "Expand Search Radius" button.
// The AJAX endpoint already returns every matching service in one response
// (posts_per_page => -1), so these are purely how many of them we paint up front.
//
// Browsing the page unfiltered is "show me the estate", so it lists everything.
// Searching a location is "what is near me", so it lists the nearest few and
// leaves the rest behind the button.
const BROWSE_RESULT_COUNT = 48;
const SEARCH_RESULT_COUNT = 9;

// How many further results that button reveals per click.
const LOAD_MORE_COUNT = 5;

/**
 * Block is used on
 *
 * @returns {{init(): void, loadMore(): void, liveInCareAvailable: boolean, sort(*): void, loading: boolean, initMap(): void, visibleResults: [], hiddenResults: [], search(): void, careService: number, sortType: number, viewType: string, location: string, locations: [], changeView(*): void, results: [], open: boolean, selected: null}}
 */
export default ({ taxonomyType = null, taxonomySlug = null, taxonomyName = null } = {}) => ({
    viewType: 'list',

    loading: true,

    results: [],
    visibleResults: [],
    hiddenResults: [],

    // Search Specific
    location: '',
    careService: -1,

    // Google Map Specific
    locations: [],

    // Dropdown
    open: false,

    sortType: 0,
    selected: null,

    // Set by init()/search(); sort() reuses it so re-sorting a search does not
    // silently re-expand it to the browse count.
    pageSize: BROWSE_RESULT_COUNT,

    liveInCareAvailable: false,
    taxonomyType: taxonomyType,
    taxonomySlug: taxonomySlug,
    taxonomyName: taxonomyName,

    // Shared by init() and search(), which page differently on purpose - init()
    // sets pageSize to BROWSE_RESULT_COUNT, search() to SEARCH_RESULT_COUNT.
    // Both must go through here: when they had separate render loops, init()
    // drew every result with no "show more" while search() drew one page, so
    // re-running the same filter appeared to lose results, and the init() copy
    // hardcoded liveInCareAvailable to false.
    applyResults(data) {
        const ref = this;

        data.data.results.forEach(function (post) {
            ref.results.push({
                id: post.id,
                title: post.title,
                permalink: post.permalink,
                post_type: post.post_type,
                image: post.image,
                image_alt: post.image_alt,
                address: post.address,
                telephone_one: post.telephone_one,
                telephone_two: post.telephone_two,
                featured_image_badge_enabled: post.featured_image_badge_enabled,
                featured_image_badge_text: post.featured_image_badge_text,
                latitude: post.latitude,
                longitude: post.longitude,
                distance: post.distance,
                google_distance: post.google_distance,
                google_duration: post.google_duration,
                tags: post.tags,
                home_care_covering_text: post.home_care_covering_text
            });
        });

        this.liveInCareAvailable = data.data.liveInCareAvailable;

        this.hiddenResults = this.hiddenResults.concat(this.results);

        this.hiddenResults.slice(0, this.pageSize).forEach(function (post) {
            ref.visibleResults.push(post);

            ref.locations.push({
                id: post.id,
                title: post.title,
                latitude: post.latitude,
                longitude: post.longitude,
                type: post.post_type,
            });

            if (post.post_type === 'home_care')
                ref.liveInCareAvailable = true;
        });

        this.hiddenResults.splice(0, this.pageSize);
        this.loading = false;
    },

    init() {
        let ref = this;

        this.loading = true;
        this.pageSize = BROWSE_RESULT_COUNT;

        this.location = (new URLSearchParams(window.location.search).get('location') || '');
        const serviceParam = (new URLSearchParams(window.location.search).get('service') || '');

        // Every service select posts a care_type term id, so that is what the param
        // normally holds. Older links carry the service name instead.
        this.careService = /^-?\d+$/.test(serviceParam)
            ? serviceParam
            : (Object.keys(legacyServiceNames).find(id => legacyServiceNames[id] === decodeURIComponent(serviceParam)) || -1);

        if (this.location.length > 0) {
            this.search();
            return;
        }

        fetch(hch_theme_ajax_endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
            },
            body: 'action=hch_fetch_carehomes&postcode=' + this.location + '&term_id=' + this.careService + '&view_type=' + this.viewType + '&taxonomy_type=' + this.taxonomyType + '&taxonomy_slug=' + this.taxonomySlug
        })
            .then((response) => {
                if (!response.ok) {
                    throw Error(response.statusText);
                }
                return response;
            })
            .then((response) => response.json())
            .then((data) => {
                this.applyResults(data);
                google.maps.event.addDomListener(window, 'load', this.initMap());
            })
            .catch((error) => {
                console.error(error);
            });
    },

    sort(sortType) {
        let ref = this;

        this.loading = true;
        this.sortType = sortType;
        this.hiddenResults = [];
        this.visibleResults = [];
        this.locations = [];
        this.liveInCareAvailable = false;

        this.hiddenResults =  this.hiddenResults.concat(this.results);

        if (sortType === 3) {
            this.hiddenResults.sort((a, b) => a.google_distance - b.google_distance)
        } else if (sortType === 4) {
            this.hiddenResults.sort((a, b) => a.google_duration - b.google_duration)
        }

        // take the first page of hidden results
        const r = this.hiddenResults.slice(0, this.pageSize);

        r.forEach(function (post) {
            ref.visibleResults.push({
                id: post.id,
                title: post.title,
                permalink: post.permalink,
                post_type: post.post_type,
                image: post.image,
                image_alt: post.image_alt,
                address: post.address,
                telephone_one: post.telephone_one,
                telephone_two: post.telephone_two,
                featured_image_badge_enabled: post.featured_image_badge_enabled,
                featured_image_badge_text: post.featured_image_badge_text,
                latitude: post.latitude,
                longitude: post.longitude,
                distance: post.distance,
                google_distance: post.google_distance,
                google_duration: post.google_duration,
                tags: post.tags,
                home_care_covering_text: post.home_care_covering_text
            })

            ref.locations.push({
                id: post.id,
                title: post.title,
                latitude: post.latitude,
                longitude: post.longitude,
                type: post.post_type,
            });
        });

        this.hiddenResults.splice(0, this.pageSize);
        this.loading = false;

        if (this.viewType === 'list')
            this.initMap()
    },

    search() {
        let ref = this;

        this.loading = true;
        this.pageSize = SEARCH_RESULT_COUNT;

        this.results = [];
        this.hiddenResults = [];
        this.visibleResults = [];
        this.locations = [];
        this.liveInCareAvailable = false;

        this.taxonomySlug = null;
        this.taxonomyType = null;
        this.taxonomyName = null;

        fetch(hch_theme_ajax_endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8'
            },
            body: 'action=hch_fetch_carehomes&postcode=' + this.location + '&term_id=' + this.careService + '&view_type=' + this.viewType + '&taxonomy_type=' + this.taxonomyType + '&taxonomy_slug=' + this.taxonomySlug
        })
            .then((response) => {
                if (!response.ok) {
                    throw Error(response.statusText);
                }
                return response;
            })
            .then((response) => response.json())
            .then((data) => {
                this.applyResults(data);
                this.initMap();
            })
            .then((data) => {
                const searchParams = new URLSearchParams();
                if (this.location) searchParams.set('location', this.location);

                // -1 is the placeholder option, 0 is "All" - both mean "do not filter".
                const serviceId = parseInt(this.careService, 10);
                if (!isNaN(serviceId) && serviceId >= 0) {
                    searchParams.set('service', serviceId);
                }

                history.pushState({}, '', '/find-a-care-service/?' + searchParams.toString());
            }).catch((error) => {
            console.error(error);
        });
    },

    loadMore() {
        this.loading = true;

        let ref = this;

        if (this.sortType === 3) {
            this.hiddenResults.sort((a, b) => a.google_distance - b.google_distance)
        } else if (this.sortType === 4) {
            this.hiddenResults.sort((a, b) => a.google_duration - b.google_duration)
        }

        const r = this.hiddenResults.slice(0, LOAD_MORE_COUNT);

        r.forEach(function (post) {
            ref.visibleResults.push({
                id: post.id,
                title: post.title,
                permalink: post.permalink,
                post_type: post.post_type,
                image: post.image,
                image_alt: post.image_alt,
                address: post.address,
                telephone_one: post.telephone_one,
                telephone_two: post.telephone_two,
                featured_image_badge_enabled: post.featured_image_badge_enabled,
                featured_image_badge_text: post.featured_image_badge_text,
                latitude: post.latitude,
                longitude: post.longitude,
                distance: post.distance,
                google_distance: post.google_distance,
                google_duration: post.google_duration,
                tags: post.tags,
                home_care_covering_text: post.home_care_covering_text
            })

            ref.locations.push({
                id: post.id,
                title: post.title,
                latitude: post.latitude,
                longitude: post.longitude,
                type: post.post_type,
            })

            if (post.post_type === 'home_care')
                ref.liveInCareAvailable = true;
        });

        this.hiddenResults.splice(0, 5);

        this.loading = false;
        this.initMap()
    },

    initMap() {
        this.loading = false;

        const map = new google.maps.Map(document.getElementById('map'), {
            zoom: 10,
            center: {
                lat: 51.900673080073005,
                lng: -0.6368104249999984
            }
        });

        let infoWindow = null;

        let latlngbounds = new google.maps.LatLngBounds();

        let ref = this;

        this.locations.forEach(function (location, index) {
            infoWindow = new google.maps.InfoWindow();

            let marker = new google.maps.Marker({
                map,
                position: {
                    lat: Number(location.latitude),
                    lng: Number(location.longitude)
                },
                title: location.title,
                label: {
                    text: (index + 1).toString(),
                    color: 'white'
                },
                icon: location.type === 'care_home' ? '/wp-content/themes/healthcarehomes/assets/images/search/pin-01.png' : '/wp-content/themes/healthcarehomes/assets/images/search/pin-02.png',
                careHomeId: location.id
            });

            latlngbounds.extend(marker.position);

            marker.addListener('click', () => {
                infoWindow.close();
                infoWindow.setContent(marker.getTitle());
                infoWindow.open(marker.getMap(), marker);

                ref.selected = marker.careHomeId;

                document.getElementById('care-home-' + marker.careHomeId).scrollIntoView({
                    behavior: 'smooth'
                });
            });
        });

        // Get the boundaries of the Map.
        let bounds = new google.maps.LatLngBounds();

        // Center map and adjust Zoom based on the position of all markers.
        map.setCenter(latlngbounds.getCenter());
        map.fitBounds(latlngbounds);
    },

    changeView(type) {
        this.viewType = type;
    }
})